import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  BookOpen,
  Plus,
  X,
  Trash2,
  Star,
  Image,
  Loader2,
  Clock,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'scenic', label: 'Landschaft', icon: '\u{1F4F8}' },
  { value: 'warning', label: 'Warnung', icon: '\u26A0\uFE0F' },
  { value: 'tip', label: 'Tipp', icon: '\u{1F4A1}' },
  { value: 'memory', label: 'Erinnerung', icon: '\u2764\uFE0F' },
];

const getCategoryIcon = (category) => {
  const cat = CATEGORIES.find((c) => c.value === category);
  return cat ? cat.icon : '\u{1F4DD}';
};

const StarRating = ({ rating, onRate, interactive = false }) => {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          className={`transition-colors ${interactive ? 'cursor-pointer hover:scale-110 active:scale-95' : 'cursor-default'}`}
          onMouseEnter={() => interactive && setHovered(star)}
          onMouseLeave={() => interactive && setHovered(0)}
          onClick={() => interactive && onRate(star)}
        >
          <Star
            className={`w-5 h-5 ${
              star <= (hovered || rating)
                ? 'text-primary fill-primary'
                : 'text-muted-foreground'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const RouteDiary = ({ routeId, isOpen, onClose }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [routeName, setRouteName] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    km_marker: '',
    note: '',
    category: 'scenic',
    rating: 0,
    photo_url: '',
  });

  const fetchEntries = useCallback(async () => {
    if (!routeId) return;
    try {
      setLoading(true);
      const response = await axios.get(`${API}/routes/${routeId}/diary/entries`);
      const data = response.data;
      setEntries(Array.isArray(data.entries) ? data.entries : Array.isArray(data) ? data : []);
      if (data.route_name) setRouteName(data.route_name);
    } catch (error) {
      console.error('Failed to fetch diary entries:', error);
      toast.error('Tagebucheintr\u00E4ge konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [routeId]);

  useEffect(() => {
    if (isOpen && routeId) {
      fetchEntries();
    }
  }, [isOpen, routeId, fetchEntries]);

  const resetForm = () => {
    setFormData({ km_marker: '', note: '', category: 'scenic', rating: 0, photo_url: '' });
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.note.trim()) {
      toast.error('Bitte einen Text eingeben');
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        km_marker: formData.km_marker ? parseFloat(formData.km_marker) : 0,
        note: formData.note.trim(),
        category: formData.category,
        rating: formData.rating,
        photo_url: formData.photo_url.trim() || null,
      };
      const response = await axios.post(`${API}/routes/${routeId}/diary/entries`, payload);
      setEntries((prev) =>
        [...prev, response.data].sort((a, b) => (a.km_marker || 0) - (b.km_marker || 0))
      );
      toast.success('Eintrag hinzugef\u00FCgt');
      resetForm();
    } catch (error) {
      console.error('Failed to add diary entry:', error);
      toast.error('Eintrag konnte nicht gespeichert werden');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/routes/${routeId}/diary/entries/${id}`);
      setEntries((prev) => prev.filter((entry) => entry.id !== id));
      toast.success('Eintrag gel\u00F6scht');
    } catch (error) {
      console.error('Failed to delete diary entry:', error);
      toast.error('Eintrag konnte nicht gel\u00F6scht werden');
    }
    setDeleteId(null);
  };

  if (!isOpen) return null;

  const sortedEntries = [...entries].sort((a, b) => (a.km_marker || 0) - (b.km_marker || 0));

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="relative w-full max-w-lg bg-background border-l border-border shadow-2xl animate-in slide-in-from-right duration-300 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <BookOpen className="w-6 h-6 text-primary" />
            <div>
              <h2 className="font-display text-xl font-bold text-foreground">
                Reisetagebuch
              </h2>
              {routeName && (
                <p className="text-sm text-muted-foreground mt-0.5">{routeName}</p>
              )}
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <ScrollArea className="flex-1">
          <div className="p-6 space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : sortedEntries.length === 0 && !showForm ? (
              <div className="text-center py-16">
                <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  Noch keine Eintr\u00E4ge
                </h3>
                <p className="text-muted-foreground">
                  Dokumentiere deine Reise!
                </p>
              </div>
            ) : (
              <>
                {/* Timeline */}
                <div className="relative">
                  {/* Timeline line */}
                  {sortedEntries.length > 1 && (
                    <div className="absolute left-5 top-8 bottom-8 w-px bg-border" />
                  )}

                  <div className="space-y-6">
                    {sortedEntries.map((entry) => (
                      <div key={entry.id} className="relative flex gap-4">
                        {/* KM Marker */}
                        <div className="flex-shrink-0 z-10">
                          <Badge className="bg-primary text-primary-foreground font-mono text-xs px-2 py-1 rounded-md shadow-md">
                            {entry.km_marker != null ? `${entry.km_marker} km` : '–'}
                          </Badge>
                        </div>

                        {/* Entry Card */}
                        <Card className="flex-1 bg-card border-border hover:border-primary/30 transition-colors">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="text-lg" role="img" aria-label={entry.category}>
                                  {getCategoryIcon(entry.category)}
                                </span>
                                <Badge variant="outline" className="text-xs border-border text-muted-foreground">
                                  {CATEGORIES.find((c) => c.value === entry.category)?.label || entry.category}
                                </Badge>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                onClick={() => setDeleteId(entry.id)}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>

                            <p className="text-foreground text-sm leading-relaxed mb-3">
                              {entry.note}
                            </p>

                            {entry.photo_url && (
                              <div className="mb-3 rounded-md overflow-hidden border border-border">
                                <img
                                  src={entry.photo_url}
                                  alt="Reisefoto"
                                  className="w-full h-32 object-cover"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              </div>
                            )}

                            <div className="flex items-center justify-between">
                              {entry.rating > 0 && (
                                <StarRating rating={entry.rating} />
                              )}
                              {entry.created_at && (
                                <div className="flex items-center gap-1 text-xs text-muted-foreground ml-auto">
                                  <Clock className="w-3 h-3" />
                                  {new Date(entry.created_at).toLocaleString('de-DE', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Add Entry Form */}
                {showForm && (
                  <>
                    <Separator className="bg-border" />
                    <Card className="bg-card border-primary/30">
                      <CardContent className="p-4">
                        <h3 className="font-display text-lg font-semibold text-foreground mb-4">
                          Neuer Eintrag
                        </h3>
                        <form onSubmit={handleSubmit} className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-foreground text-sm">Kilometer</Label>
                              <Input
                                type="number"
                                step="0.1"
                                min="0"
                                placeholder="z.B. 42.5"
                                value={formData.km_marker}
                                onChange={(e) =>
                                  setFormData({ ...formData, km_marker: e.target.value })
                                }
                                className="bg-muted/50 border-border h-10"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-foreground text-sm">Kategorie</Label>
                              <Select
                                value={formData.category}
                                onValueChange={(val) =>
                                  setFormData({ ...formData, category: val })
                                }
                              >
                                <SelectTrigger className="bg-muted/50 border-border h-10">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-card border-border">
                                  {CATEGORIES.map((cat) => (
                                    <SelectItem key={cat.value} value={cat.value}>
                                      {cat.icon} {cat.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-foreground text-sm">Notiz</Label>
                            <Textarea
                              placeholder="Was hast du erlebt?"
                              value={formData.note}
                              onChange={(e) =>
                                setFormData({ ...formData, note: e.target.value })
                              }
                              className="bg-muted/50 border-border min-h-[80px]"
                              required
                            />
                          </div>

                          <div className="space-y-2">
                            <Label className="text-foreground text-sm">Bewertung</Label>
                            <StarRating
                              rating={formData.rating}
                              onRate={(val) => setFormData({ ...formData, rating: val })}
                              interactive
                            />
                          </div>

                          <div className="space-y-2">
                            <Label className="text-foreground text-sm flex items-center gap-2">
                              <Image className="w-4 h-4" />
                              Foto-URL (optional)
                            </Label>
                            <Input
                              type="url"
                              placeholder="https://..."
                              value={formData.photo_url}
                              onChange={(e) =>
                                setFormData({ ...formData, photo_url: e.target.value })
                              }
                              className="bg-muted/50 border-border h-10"
                            />
                          </div>

                          <div className="flex gap-3 pt-2">
                            <Button
                              type="button"
                              variant="outline"
                              className="flex-1 border-border"
                              onClick={resetForm}
                            >
                              Abbrechen
                            </Button>
                            <Button
                              type="submit"
                              disabled={submitting}
                              className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground active:scale-95 transition-all"
                            >
                              {submitting ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              ) : (
                                <ChevronRight className="w-4 h-4 mr-2" />
                              )}
                              Speichern
                            </Button>
                          </div>
                        </form>
                      </CardContent>
                    </Card>
                  </>
                )}
              </>
            )}
          </div>
        </ScrollArea>

        {/* Floating Add Button */}
        {!showForm && (
          <div className="absolute bottom-6 right-6">
            <Button
              onClick={() => setShowForm(true)}
              className="h-14 w-14 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-xl active:scale-95 transition-all"
            >
              <Plus className="w-6 h-6" />
            </Button>
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Eintrag l\u00F6schen?</AlertDialogTitle>
            <AlertDialogDescription>
              Dieser Eintrag wird unwiderruflich gel\u00F6scht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-muted text-foreground border-border">
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteId)}
              className="bg-destructive text-destructive-foreground"
            >
              L\u00F6schen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RouteDiary;
